// currency-display.js

/**
 * Display-only USD/EUR toggle. Checkout stays in the store currency (USD).
 * EUR amounts: USD (dollars) × ICE_EUR_PER_USD — adjustable when wiring an API later.
 */
(function () {
  var STORAGE_KEY = 'iceCurrencyDisplay';
  /** EUR per 1 USD for display (e.g. 0.85 ≙ €0.85 per $1) */
  var ICE_EUR_PER_USD = 0.85;
  var ICE_GBP_PER_USD = 0.75;

  function hasSavedPreference() {
    try {
      var cur = localStorage.getItem(STORAGE_KEY);
      return cur === 'USD' || cur === 'EUR' || cur === 'GBP';
    } catch (e) {
      return false;
    }
  }

  function getCurrency() {
    try {
      var cur = localStorage.getItem(STORAGE_KEY);
      return cur === 'EUR' || cur === 'GBP' ? cur : 'USD';
    } catch (e) {
      return 'USD';
    }
  }

  function setCurrency(code) {
    try {
      var valid = code === 'EUR' || code === 'GBP' ? code : 'USD';
      localStorage.setItem(STORAGE_KEY, valid);
    } catch (e) {}
    document.documentElement.setAttribute('data-currency-display', getCurrency());
    applyAll();
    updateSwitcherButtons();
  }

  function formatUsdFromCents(cents) {
    var c = Number(cents);
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(c, Shopify.money_format || '${{amount}}');
    }
    return '$' + (c / 100).toFixed(2);
  }

  function formatEurFromCents(usdCents) {
    var usd = Number(usdCents) / 100;
    var eur = usd * ICE_EUR_PER_USD;
    // Round up to nearest whole euro for display (e.g. €11.46 → €12)
    var eurRounded = Math.ceil(eur);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(eurRounded);
    } catch (e) {
      return '€' + String(eurRounded);
    }
  }

  function formatGbpFromCents(usdCents) {
    var usd = Number(usdCents) / 100;
    var gbp = usd * ICE_GBP_PER_USD;

    // Round up to nearest whole pound
    var gbpRounded = Math.ceil(gbp);

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'GBP',
        maximumFractionDigits: 0
      }).format(gbpRounded);
    } catch (e) {
      return '£' + String(gbpRounded);
    }
  }

  function formatMoneyForDisplay(cents) {
    var currency = getCurrency();

    if (currency === 'EUR') {
      return formatEurFromCents(cents);
    }

    if (currency === 'GBP') {
      return formatGbpFromCents(cents);
    }

    return formatUsdFromCents(cents);
  }

  /**
   * Intelligems price-test awareness.
   * For a tagged regular price (.igPrice with a data-variant-id), use Intelligems'
   * test price (store USD dollars) as the source cents so the display-currency
   * conversion below reflects the running price test in USD/GBP/EUR alike.
   * Returns null when there is no IG data yet or the element isn't a tagged price,
   * in which case the hardcoded data-ice-money-cents value is used as before.
   */
  function igCentsFor(el) {
    try {
      if (!el.classList || !el.classList.contains('igPrice')) return null;
      var vid = el.getAttribute('data-variant-id');
      if (!vid) return null;
      var ig = window.igData && window.igData.price;
      if (!ig || typeof ig.getPriceByVariantId !== 'function') return null;
      var dollars = ig.getPriceByVariantId(vid);
      if (typeof dollars !== 'number' || isNaN(dollars)) return null;
      return Math.round(dollars * 100);
    } catch (e) {
      return null;
    }
  }

  function applyAll() {
    document.querySelectorAll('[data-ice-money-cents]').forEach(function (el) {
      if (el.closest('[data-ice-skip-container]')) return;

      var raw = el.getAttribute('data-ice-money-cents');
      if (raw === null || raw === '') return;

      var cents = Number(raw);

      // Prefer the Intelligems test price for tagged regular-price elements.
      var igCents = igCentsFor(el);
      if (igCents !== null) cents = igCents;

      if (el.hasAttribute('data-ice-hide-if-zero') && cents === 0) {
        el.textContent = '';
        return;
      }

      el.textContent = formatMoneyForDisplay(cents);
    });
  }

  function updateSwitcherButtons() {
    var cur = getCurrency();
    document.querySelectorAll('[data-ice-currency]').forEach(function (btn) {
      var isSel = btn.getAttribute('data-ice-currency') === cur;
      btn.classList.toggle('is-active', isSel);
      btn.setAttribute('aria-pressed', isSel ? 'true' : 'false');
    });
  }

  function bindSwitcher() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-ice-currency]');
      if (!btn) return;
      e.preventDefault();
      var code = btn.getAttribute('data-ice-currency');
      // if (code === 'USD' || code === 'EUR') setCurrency(code);
      if (code === 'USD' || code === 'EUR' || code === 'GBP') {
        setCurrency(code);
      }
    });
  }

  function fetchWithTimeout(url, opts, timeoutMs) {
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = null;
    var o = opts || {};
    if (controller) o.signal = controller.signal;
    timeoutMs = timeoutMs || 2200;

    if (controller) {
      timer = setTimeout(function () {
        try {
          controller.abort();
        } catch (e) {}
      }, timeoutMs);
    }

    return fetch(url, o).finally(function () {
      if (timer) clearTimeout(timer);
    });
  }

  function shouldDefaultToEur(geo) {
    if (!geo || typeof geo !== 'object') return false;
    var cc = (geo.country_code || geo.countryCode || geo.country || '').toString().toUpperCase();
    var continent = (geo.continent_code || geo.continentCode || geo.continent || '').toString().toUpperCase();

    // UK should stay USD by default (UK uses GB in most APIs)
    if (cc === 'GB' || cc === 'UK') return false;

    // Europe (continent_code EU), excluding UK
    return continent === 'EU' || continent === 'EUROPE';
  }

  function isEuCountryCode(cc) {
    cc = (cc || '').toString().toUpperCase();
    // EU + EEA + Switzerland (common “Europe pricing” expectation), explicitly excluding UK/GB.
    // If you want EU-only (exclude NO/IS/LI/CH), tell me and I’ll tighten this list.
    var EUROPE_CODES = {
      AT: 1, BE: 1, BG: 1, HR: 1, CY: 1, CZ: 1, DK: 1, EE: 1, FI: 1, FR: 1, DE: 1, GR: 1, HU: 1, IE: 1,
      IT: 1, LV: 1, LT: 1, LU: 1, MT: 1, NL: 1, PL: 1, PT: 1, RO: 1, SK: 1, SI: 1, ES: 1, SE: 1,
      // EEA/EFTA/CH
      NO: 1, IS: 1, LI: 1, CH: 1
    };
    if (cc === 'GB' || cc === 'UK') return false;
    return !!EUROPE_CODES[cc];
  }

  // function detectDefaultFromShopifyLocalization() {
  //   try {
  //     var cc = document.documentElement.getAttribute('data-ice-country');
  //     if (!cc) return null;
  //     return isEuCountryCode(cc) ? 'EUR' : 'USD';
  //   } catch (e) {
  //     return null;
  //   }
  // }

  function detectDefaultFromShopifyLocalization() {
    try {
      var cc = document.documentElement.getAttribute('data-ice-country');
      console.log("CC ==>>", cc);

      if (!cc) return null;

      cc = cc.toUpperCase();

      // UK → GBP
      if (cc === 'GB' || cc === 'UK') {
        return 'GBP';
      }

      // Europe → EUR
      if (isEuCountryCode(cc)) {
        return 'EUR';
      }

      // Everyone else → USD
      return 'USD';
    } catch (e) {
      return null;
    }
  }

  function detectAndSetDefaultCurrency() {
    // Only run when user hasn't explicitly chosen before
    if (hasSavedPreference()) return;

    // Avoid repeated lookups in same session
    if (window.__iceCurrencyGeoChecked) return;
    window.__iceCurrencyGeoChecked = true;

    // 1) Prefer Shopify's server-side geo (works even with adblock/VPN extensions)
    var shopifyDefault = detectDefaultFromShopifyLocalization();
    if (shopifyDefault === 'GBP') {
      setCurrency('GBP');
      return;
    }
    if (shopifyDefault === 'EUR') {
      setCurrency('EUR');
      return;
    }
    if (shopifyDefault === 'USD') {
      setCurrency('USD');
      return;
    }

    // 2) Fallback: external GeoIP (may be blocked by privacy tools)
    fetchWithTimeout('https://ipwho.is/?fields=success,continent_code,country_code', { method: 'GET' }, 2200)
      .then(function (res) { return res && res.ok ? res.json() : null; })
      .then(function (json) {
        if (!json || json.success === false) return;
        // if (shouldDefaultToEur(json)) {
        //   setCurrency('EUR');
        // } else {
        //   // explicitly store USD so subsequent loads don't re-check
        //   setCurrency('USD');
        // }
        var cc = (json.country_code || '').toUpperCase();

        // UK → GBP
        if (cc === 'GB' || cc === 'UK') {
          setCurrency('GBP');
          return;
        }

        // Europe → EUR
        if (shouldDefaultToEur(json)) {
          setCurrency('EUR');
          return;
        }

        // Everyone else → USD
        setCurrency('USD');
      })
      .catch(function () {
        // keep USD, don't store preference
      });
  }

  window.iceCurrencyApplyPrices = applyAll;
  window.iceCurrencyGetDisplay = getCurrency;
  window.iceCurrencySetDisplay = setCurrency;

  bindSwitcher();

  function boot() {
    document.documentElement.setAttribute('data-currency-display', getCurrency());
    applyAll();
    updateSwitcherButtons();
    detectAndSetDefaultCurrency();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('cart:updated', function () {
    requestAnimationFrame(function () {
      applyAll();
    });
  });

  window.ICE_EUR_PER_USD = ICE_EUR_PER_USD;
})();

